import { useEffect, useState, type CSSProperties } from 'react'
import type { ExerciseDefinition } from '../types'
import { ExerciseDiagram } from './ExerciseGuide'

interface ExerciseMediaProps {
  exercise: ExerciseDefinition
  compact?: boolean
}

type MediaStyle = CSSProperties & {
  '--frame-count': number
  '--phase-shift': string
  '--panel-ratio': number
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function ExerciseMedia({ exercise, compact = false }: ExerciseMediaProps) {
  const frames = exercise.mediaFrames ?? 1
  const [phase, setPhase] = useState(0)
  const [playing, setPlaying] = useState(!prefersReducedMotion())
  const [panelRatio, setPanelRatio] = useState(0.72)
  const [failed, setFailed] = useState(false)
  const isDiagram = exercise.visualStyle === 'diagram'

  useEffect(() => {
    setPhase(0)
    setFailed(false)
    setPlaying(!prefersReducedMotion())
  }, [exercise.id])

  useEffect(() => {
    if (!playing || frames < 2 || isDiagram) return
    const timer = window.setInterval(() => setPhase((current) => (current + 1) % frames), 1500)
    return () => window.clearInterval(timer)
  }, [frames, isDiagram, playing])

  const style: MediaStyle = {
    '--frame-count': frames,
    '--phase-shift': `${-(phase / frames) * 100}%`,
    '--panel-ratio': panelRatio
  }

  return <div className={`exercise-visual${compact ? ' exercise-visual--compact' : ''}${playing ? ' is-playing' : ' is-paused'}`}>
    {isDiagram ? (
      <ExerciseDiagram exercise={exercise} compact />
    ) : failed || !exercise.media ? (
      <div className="exercise-visual__fallback"><strong>Изображение недоступно</strong><span>Следуй шагам и остановись при боли.</span></div>
    ) : (
      <div className="exercise-visual__viewport" style={style}>
        <div className="exercise-visual__crop">
          <img
            src={exercise.media}
            alt={exercise.mediaAlt}
            draggable={false}
            onLoad={(event) => setPanelRatio((event.currentTarget.naturalWidth / frames) / event.currentTarget.naturalHeight)}
            onError={() => setFailed(true)}
          />
        </div>
      </div>
    )}

    {!compact && <div className="exercise-visual__controls">
      {isDiagram ? <div className="exercise-visual__phase-notes" aria-label="Фазы движения">
        {Array.from({ length: 2 }, (_, index) => <span key={index}><b>{index + 1}</b>{exercise.frameLabels?.[index] || (index === 0 ? 'Старт' : 'Движение')}</span>)}
      </div> : <div className="exercise-visual__phases" role="group" aria-label="Фазы движения">
        {Array.from({ length: frames }, (_, index) => <button
          key={index}
          type="button"
          className={phase === index ? 'is-current' : ''}
          aria-label={exercise.frameLabels?.[index] || `Фаза ${index + 1}`}
          aria-pressed={phase === index}
          onClick={() => { setPhase(index); setPlaying(false) }}
        ><span>{index + 1}</span><small>{exercise.frameLabels?.[index] || (index === 0 ? 'Старт' : 'Движение')}</small></button>)}
      </div>}
      {(isDiagram || frames > 1) && <button className="exercise-visual__play" type="button" aria-label={playing ? 'Остановить демонстрацию' : 'Включить демонстрацию'} onClick={() => setPlaying((value) => !value)}>{playing ? 'Ⅱ' : '▶'}</button>}
    </div>}
  </div>
}
